import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

type ExploitationStatus =
  | 'none'
  | 'poc'
  | 'active'
  | 'widespread'
  | null
  | undefined

interface Props {
  previouslyKevFlagged: boolean | null | undefined
  exploitationStatus: ExploitationStatus
  kevRemovedAt?: string | null
  kevFirstSeenAt?: string | null
  className?: string
}

/**
 * V2 — CISA-KEV de-escalation pill.
 *
 * Renders "Previously flagged as actively exploited" when a CVE was once in
 * the CISA KEV catalogue and has since been retired. Hidden when:
 *   - `previouslyKevFlagged` is false/null — the CVE was never in KEV,
 *   - `exploitationStatus === 'active'` — the CVE is currently flagged, so
 *     the "active" pill takes precedence.
 *
 * Tooltip shows the KEV lifecycle timestamps.
 *
 * See 2026-04-20-vulns-v2-kev-deescalation-design.md §3.1.
 */
export function PreviouslyKevFlaggedPill({
  previouslyKevFlagged,
  exploitationStatus,
  kevRemovedAt,
  kevFirstSeenAt,
  className,
}: Props) {
  if (!previouslyKevFlagged) return null
  if (exploitationStatus === 'active') return null

  const tooltipParts: string[] = []
  if (kevRemovedAt) {
    tooltipParts.push(`Removed from CISA KEV on ${formatDate(kevRemovedAt)}.`)
  }
  if (kevFirstSeenAt) {
    tooltipParts.push(`First added ${formatDate(kevFirstSeenAt)}.`)
  }
  const tooltip =
    tooltipParts.length > 0
      ? tooltipParts.join(' ')
      : 'Previously listed in CISA KEV.'

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="secondary"
            className={cn(
              // Muted amber — visually weaker than the active "Actively Exploited (KEV)"
              // pill but distinct from baseline. Dark-mode handled via Tailwind variants.
              'cursor-help bg-amber-50 text-amber-800 border border-amber-200',
              'dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-900/60',
              className,
            )}
            aria-label="Previously flagged as actively exploited"
          >
            Previously flagged as actively exploited
          </Badge>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function formatDate(iso: string): string {
  // Renders as YYYY-MM-DD (UTC) — matches the design spec.
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
